import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isPremium: boolean;
  loading: boolean;
  isPasswordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkPremiumStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const clearPasswordRecovery = () => {
    setIsPasswordRecovery(false);
    // Clear the URL hash if it contains recovery params
    if (window.location.hash.includes('type=recovery')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };


  // Premium check runs only with a ready session passed in by the caller,
  // and concurrent calls for the same token share one request.
  const premiumInFlight = useRef<{ token: string; promise: Promise<void> } | null>(null);
  const initialAuthDone = useRef(false);

  const checkPremiumStatus = (readySession: Session | null): Promise<void> => {
    if (!readySession?.access_token) {
      setIsPremium(false);
      return Promise.resolve();
    }
    const token = readySession.access_token;
    if (premiumInFlight.current?.token === token) return premiumInFlight.current.promise;

    const promise = (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("check-premium", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (error) {
          // Request aborted by navigation/tab close — retry happens on next auth event.
          if ((error as { name?: string }).name === "FunctionsFetchError") return;
          // Token was superseded (refresh/sign-out) while in flight — not an error.
          const { data: { session: latest } } = await supabase.auth.getSession();
          if (latest?.access_token !== token) return;
          console.warn("Premium status unavailable:", error.message);
          return; // keep previous value; don't flip a Pro user to free
        }
        setIsPremium(data?.has_purchased_starter || false);
      } catch {
        // Network interruption — keep the previous value silently.
      } finally {
        if (premiumInFlight.current?.token === token) premiumInFlight.current = null;
      }
    })();
    premiumInFlight.current = { token, promise };
    return promise;
  };

  useEffect(() => {
    let isMounted = true;

    // Helper to check for recovery indicators
    const checkRecoveryUrl = () => {
      return window.location.hash.includes('type=recovery') || 
             window.location.href.includes('type=recovery') ||
             sessionStorage.getItem('password_recovery_pending') === 'true';
    };

    // ONGOING auth state changes (does NOT control loading after initial load)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!isMounted) return;
        const currentPath = window.location.pathname;
        console.log("Auth event:", event, "Current path:", currentPath, "Session:", newSession ? "exists" : "null", "Provider:", newSession?.user?.app_metadata?.provider);
        
        // CRITICAL: If we're on /reset-password, DO NOT interfere
        if (currentPath === '/reset-password') {
          console.log("🎯 On /reset-password - AuthContext hands off control");
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          return;
        }

        // Detect OAuth sign-in
        const isOAuth = newSession?.user?.app_metadata?.provider &&
          newSession.user.app_metadata.provider !== 'email';

        // APP-WIDE GUARD: Clear recovery flags for authenticated users not on reset page
        if (newSession?.user && currentPath !== '/reset-password') {
          const hasRecoveryInUrl = window.location.hash.includes('type=recovery');
          if (hasRecoveryInUrl) {
            console.log("🎯 Authenticated user with recovery hash - clearing and ignoring");
            window.history.replaceState(null, '', window.location.pathname);
            sessionStorage.removeItem('password_recovery_pending');
            sessionStorage.removeItem('password_recovery_hash');
            setIsPasswordRecovery(false);
          }
        }

        // Handle PASSWORD_RECOVERY event
        if (event === "PASSWORD_RECOVERY" && currentPath !== '/reset-password') {
          console.log("🎯 PASSWORD_RECOVERY event - redirecting to reset page");
          setIsPasswordRecovery(true);
          sessionStorage.setItem('password_recovery_pending', 'true');
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          window.location.replace('/reset-password?type=recovery');
          return;
        }

        const isRecovery = checkRecoveryUrl();
        
        // For SIGNED_IN during recovery on reset-password page only
        if (event === "SIGNED_IN" && isRecovery && currentPath === '/reset-password') {
          console.log("🎯 SIGNED_IN during recovery on reset page");
          setIsPasswordRecovery(true);
          setSession(newSession);
          setUser(newSession?.user ?? null);
          setLoading(false);
          return;
        }

        // SIGNED_OUT: clear state
        if (event === "SIGNED_OUT") {
          console.log("🎯 SIGNED_OUT event - clearing all auth state");
          setSession(null);
          setUser(null);
          setIsPremium(false);
          setLoading(false);
          return;
        }

        // Initial session is handled by initializeAuth (which awaits the
        // premium check before ending loading), so skip it here.
        if (event === "INITIAL_SESSION") return;

        // ALWAYS set session/user state immediately
        console.log("Auth State Changed:", newSession ? { user: newSession.user?.email, provider: newSession.user?.app_metadata?.provider } : null);
        setSession(newSession);
        setUser(newSession?.user ?? null);
        if (initialAuthDone.current) setLoading(false);

        // OAuth SIGNED_IN: ensure profile exists (deferred, non-blocking)
        if (event === "SIGNED_IN" && isOAuth && newSession?.user) {
          console.log("🎯 OAuth sign-in detected (provider:", newSession.user.app_metadata.provider, ")");
          setTimeout(async () => {
            try {
              const { data: profile } = await supabase
                .from("profiles")
                .select("id")
                .eq("user_id", newSession.user.id)
                .maybeSingle();

              if (!profile) {
                console.log("🎯 Creating profile for new OAuth user");
                await supabase.from("profiles").insert({
                  user_id: newSession.user.id,
                  has_purchased_starter: false,
                  shield_credits: 3,
                });
              }
            } catch (profileErr) {
              console.error("🎯 Profile check/create failed (non-fatal):", profileErr);
            }
          }, 0);
        }

        // Premium check (deferred, non-blocking)
        if (newSession?.user && !isRecovery) {
          setTimeout(() => checkPremiumStatus(newSession), 0);
        } else if (!newSession) {
          setIsPremium(false);
        }
      }
    );

    // INITIAL load — separate from onAuthStateChange to avoid race conditions
    const initializeAuth = async () => {
      try {
        const hash = window.location.hash;
        const hashContainsToken = hash.includes('access_token');
        let finalSession: Session | null = null;

        // FORCE INJECT SESSION FIRST: If tokens are in the hash, extract and
        // set the session manually BEFORE calling getSession. This prevents the
        // race condition where getSession returns null while the hash is present.
        if (hashContainsToken && hash) {
          console.log("🔑 Found tokens in hash — force injecting session");
          const params = new URLSearchParams(hash.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            const { data, error: setErr } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (setErr) {
              console.error("🔑 Force setSession failed:", setErr);
              // Fall back to getSession
              const { data: { session: fallback } } = await supabase.auth.getSession();
              finalSession = fallback;
            } else {
              console.log("🔑 Force setSession succeeded");
              finalSession = data.session;
            }
          } else {
            // Hash present but tokens missing — fall back
            const { data: { session: fallback } } = await supabase.auth.getSession();
            finalSession = fallback;
          }
        } else {
          // No hash tokens — normal session retrieval
          const { data: { session: normalSession } } = await supabase.auth.getSession();
          finalSession = normalSession;
        }

        if (!isMounted) return;

        console.log("🔍 Session result:", finalSession ? "session exists" : "no session");

        // Clean the hash from the URL once session is established
        if (finalSession && window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        const isRecovery = checkRecoveryUrl();
        
        if (isRecovery) {
          console.log("🎯 Recovery detected on getSession - setting state only");
          setIsPasswordRecovery(true);
          setSession(finalSession);
          setUser(finalSession?.user ?? null);
          setLoading(false);
          return;
        }

        setSession(finalSession);
        setUser(finalSession?.user ?? null);
        
        if (finalSession?.user) {
          // Await premium check before setting loading false so UI doesn't flash
          await checkPremiumStatus(finalSession);
        }
      } catch (err) {
        console.error("🔍 initializeAuth error:", err);
      } finally {
        initialAuthDone.current = true;
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    // Attach attribution metadata so handle_new_user trigger can persist it
    const { getAttribution } = await import("@/lib/attribution");
    const attribution = getAttribution();
    console.log("[signup] attaching attribution", attribution);

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: {
          source: attribution.source ?? null,
          first_touch: attribution.first_touch ?? null,
          campaign: attribution.campaign ?? null,
          content: attribution.content ?? null,
          hook: attribution.hook ?? null,
        },
      },
    });

    return { error: error ? new Error(error.message) : null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error && error.message === "Invalid login credentials") {
      // Check if the email exists by attempting a dummy signUp
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password: crypto.randomUUID(),
        options: { emailRedirectTo: `${window.location.origin}/login` },
      });
      
      if (!signUpError || !signUpError.message?.includes("already registered")) {
        return { error: new Error("Account not found. Please create an account first.") };
      }
      return { error: new Error("Invalid email or password.") };
    }

    // If login succeeded, ensure a profile row exists
    if (!error && data?.user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", data.user.id)
        .maybeSingle();

      if (!profile) {
        await supabase.from("profiles").insert({
          user_id: data.user.id,
          has_purchased_starter: false,
          shield_credits: 3,
        });
      }
    }

    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    // Clear all Supabase-related local storage keys before signing out
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    sessionStorage.clear();

    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsPremium(false);
    // Force redirect to home after logout
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isPremium,
        loading,
        isPasswordRecovery,
        clearPasswordRecovery,
        signUp,
        signIn,
        signOut,
        checkPremiumStatus: async () => {
          const { data: { session: s } } = await supabase.auth.getSession();
          await checkPremiumStatus(s);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
